import { useEffect, useRef, useState } from 'react';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, X,
  UserPlus, Shapes, MinusCircle, Loader2, Info,
} from 'lucide-react';
import { Badge, Button, Modal } from '../components/ui';
import { useImportMembers, type ImportResponse, type ImportRowResult } from '../api/queries';
import { readTable, type Grid } from '../lib/xlsx-read';
import { useToast } from '../lib/toast';

// Spalten werden über EXAKTE Kopfnamen (nach Normalisierung) erkannt, nicht über
// Teilstrings: „Zahlungsart" darf nicht als Mitgliedsart und „Geburtsort" nicht
// als Geburtsdatum durchgehen. Nicht erkannte Spalten werden im Modal angezeigt,
// damit nichts stillschweigend verloren geht.
const FIELDS: { field: string; label: string; aliases: string[] }[] = [
  { field: 'sektion',        label: 'Sektion',        aliases: ['sektion', 'sparte', 'abteilung'] },
  { field: 'vorname',        label: 'Vorname',        aliases: ['vorname'] },
  { field: 'nachname',       label: 'Nachname',       aliases: ['nachname', 'familienname', 'zuname'] },
  { field: 'geschlecht',     label: 'Geschlecht',     aliases: ['geschlecht'] },
  { field: 'geburtsdatum',   label: 'Geburtsdatum',   aliases: ['geburtsdatum', 'geb datum', 'geburtstag', 'geboren', 'geb'] },
  { field: 'strasse',        label: 'Straße',         aliases: ['strasse & hausnummer', 'straße & hausnummer', 'strasse', 'straße', 'adresse', 'anschrift', 'strasse hausnummer', 'straße hausnummer'] },
  { field: 'plz',            label: 'PLZ',            aliases: ['plz', 'postleitzahl'] },
  { field: 'ort',            label: 'Ort',            aliases: ['ort', 'wohnort', 'gemeinde', 'stadt'] },
  { field: 'telefon',        label: 'Telefon',        aliases: ['telefon', 'telefonnummer', 'tel', 'tel nr', 'telefon nr', 'mobil', 'handy', 'mobilnummer'] },
  { field: 'email',          label: 'E-Mail',         aliases: ['e mail', 'email', 'mail', 'e mail adresse', 'mailadresse'] },
  { field: 'funktion',       label: 'Funktion',       aliases: ['funktion', 'rolle', 'amt'] },
  { field: 'mitgliedsart',   label: 'Mitgliedsart',   aliases: ['mitgliedsart', 'art', 'mitgliedschaft'] },
  { field: 'eingetreten_am', label: 'Eintrittsdatum', aliases: ['eintrittsdatum', 'eintritt', 'eingetreten', 'beitrittsdatum', 'mitglied seit', 'eingetreten am'] },
  { field: 'anmerkungen',    label: 'Anmerkungen',    aliases: ['anmerkungen', 'anmerkung', 'bemerkungen', 'bemerkung', 'veränderungen', 'veraenderungen', 'notiz', 'notizen', 'kommentar'] },
];

/**
 * Normalisiert eine Kopfzelle für den Abgleich.
 * „Geb.Datum" → „geb datum" · „E-Mail *" → „e mail" · „Sektion * ▾" → „sektion"
 *
 * Bewusst als Positivliste (Buchstaben, Ziffern, &) statt als Liste verbotener
 * Zeichen: sonst reicht ein neues Symbol im Kopf — etwa das ▾ der Dropdown-
 * Spalten — und die Spalte wird nicht mehr erkannt.
 */
const clean = (s: string) =>
  (s ?? '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}&]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const ALIAS_TO_FIELD = new Map<string, string>();
for (const f of FIELDS) for (const a of f.aliases) if (!ALIAS_TO_FIELD.has(a)) ALIAS_TO_FIELD.set(a, f.field);

const LABEL_OF = new Map(FIELDS.map((f) => [f.field, f.label]));

interface Parsed {
  rows: Record<string, string>[];
  skippedExample: number;
  missing: string[];
  mapped: string[];
  ignored: string[];
}

function parseGrid(grid: Grid): Parsed {
  // Kopfzeile = erste Zeile, die Vorname UND Nachname enthält
  let headerIdx = -1;
  for (let i = 0; i < Math.min(grid.length, 30); i++) {
    const cells = (grid[i] ?? []).map(clean);
    if (cells.some((c) => c.includes('vorname')) && cells.some((c) => c.includes('nachname'))) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx < 0) {
    throw new Error('Keine Kopfzeile gefunden — bitte die unveränderte Vorlage verwenden (Spalten „Vorname"/„Nachname").');
  }

  const rawHeader = grid[headerIdx] ?? [];
  const header = rawHeader.map(clean);
  const colOf = new Map<string, number>();
  const ignored: string[] = [];

  header.forEach((h, ci) => {
    if (!h) return;
    const field = ALIAS_TO_FIELD.get(h);
    if (field && !colOf.has(field)) colOf.set(field, ci);
    else if (!field) ignored.push((rawHeader[ci] ?? '').trim() || `Spalte ${ci + 1}`);
  });

  const missing = ['sektion', 'vorname', 'nachname'].filter((f) => !colOf.has(f));
  const mapped = [...colOf.keys()].map((f) => LABEL_OF.get(f) ?? f);

  const rows: Record<string, string>[] = [];
  let skippedExample = 0;

  for (let i = headerIdx + 1; i < grid.length; i++) {
    const line = grid[i] ?? [];
    const at = (f: string) => {
      const ci = colOf.get(f);
      return ci === undefined ? '' : (line[ci] ?? '').trim();
    };
    const vorname = at('vorname');
    const nachname = at('nachname');
    if (!vorname && !nachname) continue; // Leerzeile

    const row: Record<string, string> = { zeile: String(i + 1) };
    for (const f of colOf.keys()) row[f] = at(f);

    // Beispielzeile der Vorlage überspringen
    const blob = `${row.anmerkungen ?? ''} ${row.email ?? ''}`.toLowerCase();
    if (blob.includes('beispielzeile') || blob.includes('max.mustermann@example.at')) {
      skippedExample++;
      continue;
    }
    rows.push(row);
  }

  return { rows, skippedExample, missing, mapped, ignored };
}

const CHUNK = 100;

/**
 * Zerlegt die Zeilen in Blöcke — ein Worker-Aufruf darf nur begrenzt viele
 * D1-Abfragen machen. Sortiert vorher nach Name, damit Zeilen derselben Person
 * garantiert im selben Block landen (sonst würde die Vorschau sie doppelt als
 * „neu" melden) und schneidet nie mitten in einer Namensgruppe.
 */
function toChunks(rows: Record<string, string>[]): Record<string, string>[][] {
  const keyOf = (r: Record<string, string>) =>
    `${(r.vorname ?? '').trim().toLowerCase()}|${(r.nachname ?? '').trim().toLowerCase()}`;
  const sorted = [...rows].sort((a, b) => keyOf(a).localeCompare(keyOf(b), 'de'));

  const chunks: Record<string, string>[][] = [];
  let cur: Record<string, string>[] = [];
  for (let i = 0; i < sorted.length; i++) {
    cur.push(sorted[i]!);
    const sameAsNext = i + 1 < sorted.length && keyOf(sorted[i + 1]!) === keyOf(sorted[i]!);
    if (cur.length >= CHUNK && !sameAsNext) { chunks.push(cur); cur = []; }
  }
  if (cur.length > 0) chunks.push(cur);
  return chunks;
}

const STATUS_META: Record<ImportRowResult['status'], { label: string; tone: 'success' | 'info' | 'neutral' | 'danger'; icon: typeof UserPlus }> = {
  neu:              { label: 'neu',             tone: 'success', icon: UserPlus },
  sektion_ergaenzt: { label: 'Sektion ergänzt', tone: 'info',    icon: Shapes },
  vorhanden:        { label: 'schon erfasst',   tone: 'neutral', icon: MinusCircle },
  fehler:           { label: 'Fehler',          tone: 'danger',  icon: AlertTriangle },
};

export function ImportMembersModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const imp = useImportMembers();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [preview, setPreview] = useState<ImportResponse | null>(null);
  const [done, setDone] = useState<ImportResponse | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [partial, setPartial] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFileName(''); setParsing(false); setParseErr(null);
    setParsed(null); setPreview(null); setDone(null); setDragOver(false);
    setProgress(null); setPartial(null);
    imp.reset();
  }, [open]);

  /** Führt den Import blockweise aus und fügt die Teilergebnisse zusammen. */
  const runChunked = async (rows: Record<string, string>[], dryRun: boolean): Promise<ImportResponse> => {
    const chunks = toChunks(rows);
    const merged: ImportResponse = {
      dryRun,
      summary: { total: 0, created: 0, sectionAdded: 0, unchanged: 0, failed: 0 },
      results: [],
    };
    setProgress({ done: 0, total: rows.length });
    let sent = 0;
    for (const part of chunks) {
      try {
        const res = await imp.mutateAsync({ rows: part, dryRun });
        merged.summary.total += res.summary.total;
        merged.summary.created += res.summary.created;
        merged.summary.sectionAdded += res.summary.sectionAdded;
        merged.summary.unchanged += res.summary.unchanged;
        merged.summary.failed += res.summary.failed;
        merged.results.push(...res.results);
      } catch (e) {
        // Beim echten Lauf können frühere Blöcke schon geschrieben sein — das
        // muss der Nutzer erfahren, statt nur „fehlgeschlagen" zu sehen.
        if (!dryRun && sent > 0) {
          setPartial(
            `Der Import wurde nach ${sent} von ${rows.length} Zeilen abgebrochen. ` +
            'Die bereits verarbeiteten Zeilen sind gespeichert — die Datei kann einfach ' +
            'erneut hochgeladen werden, bereits Angelegte werden dann als „schon erfasst" erkannt.',
          );
          break;
        }
        throw e;
      }
      sent += part.length;
      setProgress({ done: sent, total: rows.length });
    }
    // Anzeige in Dateireihenfolge, nicht in Verarbeitungsreihenfolge
    merged.results.sort((a, b) => a.zeile - b.zeile);
    setProgress(null);
    return merged;
  };

  const handleFile = async (file: File) => {
    setParsing(true); setParseErr(null); setParsed(null); setPreview(null); setDone(null);
    setFileName(file.name);
    try {
      const grid = await readTable(file);
      const p = parseGrid(grid);
      if (p.missing.length > 0) {
        throw new Error(`Diese Spalten fehlen in der Datei: ${p.missing.join(', ')}.`);
      }
      if (p.rows.length === 0) {
        throw new Error('Die Datei enthält keine ausgefüllten Zeilen.');
      }
      setParsed(p);
      // Direkt eine Prüfung (dryRun) hinterher — zeigt neu vs. schon vorhanden
      setPreview(await runChunked(p.rows, true));
    } catch (e: any) {
      setParseErr(e?.message ?? 'Die Datei konnte nicht gelesen werden.');
    } finally {
      setParsing(false);
    }
  };

  const runImport = async () => {
    if (!parsed) return;
    setPartial(null);
    try {
      const res = await runChunked(parsed.rows, false);
      setDone(res);
      const s = res.summary;
      toast.push(`${s.created} Mitglieder importiert${s.sectionAdded ? `, ${s.sectionAdded} Sektion ergänzt` : ''}`);
    } catch {
      setProgress(null);
      toast.push('Import fehlgeschlagen — es wurde nichts gespeichert', 'warn');
    }
  };

  const shown = done ?? preview;
  const canImport = !!preview && !done && (preview.summary.created > 0 || preview.summary.sectionAdded > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Mitglieder importieren"
      size="lg"
      footer={
        done ? (
          <>
            <div style={{ flex: 1 }} />
            <Button variant="primary" onClick={onClose}>Fertig</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
            <div style={{ flex: 1 }} />
            <Button
              variant="primary"
              icon={imp.isPending && parsed ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
              disabled={!canImport || imp.isPending || parsing}
              onClick={runImport}
            >
              {preview
                ? `${preview.summary.created + preview.summary.sectionAdded} Einträge importieren`
                : 'Importieren'}
            </Button>
          </>
        )
      }
    >
      {/* --- Datei-Auswahl --- */}
      {!shown && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void handleFile(f);
          }}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `1.5px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`,
            background: dragOver ? 'var(--accent-soft)' : 'var(--bg-muted)',
            borderRadius: 12,
            padding: '34px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 160ms var(--ease-apple)',
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm,.csv"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = '';
            }}
          />
          {parsing ? (
            <>
              <Loader2 size={26} className="spin" style={{ color: 'var(--accent)' }} />
              <div style={{ marginTop: 10, fontSize: 14, fontWeight: 500 }}>„{fileName}" wird gelesen…</div>
              {progress && (
                <div className="text-tertiary" style={{ fontSize: 12.5, marginTop: 4 }}>
                  {progress.done} von {progress.total} Zeilen geprüft
                </div>
              )}
            </>
          ) : (
            <>
              <FileSpreadsheet size={26} style={{ color: 'var(--accent)' }} />
              <div style={{ marginTop: 10, fontSize: 15, fontWeight: 600 }}>
                Ausgefüllte Excel-Vorlage hier ablegen
              </div>
              <div className="text-tertiary" style={{ fontSize: 13, marginTop: 4 }}>
                oder klicken zum Auswählen · .xlsx oder .csv
              </div>
            </>
          )}
        </div>
      )}

      {parseErr && (
        <div
          style={{
            marginTop: 14, display: 'flex', gap: 10, alignItems: 'flex-start',
            padding: '12px 14px', borderRadius: 10,
            background: 'var(--danger-soft, rgba(255,59,48,0.09))',
          }}
        >
          <AlertTriangle size={16} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13.5 }}>{parseErr}</div>
        </div>
      )}

      {/* --- Vorschau / Ergebnis --- */}
      {shown && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="import-filebar">
            <FileSpreadsheet size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <span className="fname">{fileName}</span>
            <span className="text-tertiary fmeta">
              {shown.summary.total} Zeile{shown.summary.total === 1 ? '' : 'n'}
              {parsed?.skippedExample ? ' · Beispielzeile übersprungen' : ''}
            </span>
            {!done && (
              <Button variant="ghost" size="sm" icon={<X size={14} />} onClick={() => {
                setParsed(null); setPreview(null); setFileName(''); setParseErr(null);
              }}>
                Andere Datei
              </Button>
            )}
          </div>

          {/* Zusammenfassung */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <SumTile n={shown.summary.created} label={done ? 'neu angelegt' : 'werden neu angelegt'} tone="success" />
            {shown.summary.sectionAdded > 0 && (
              <SumTile n={shown.summary.sectionAdded} label="Sektion ergänzt" tone="info" />
            )}
            {shown.summary.unchanged > 0 && (
              <SumTile n={shown.summary.unchanged} label="schon erfasst" tone="neutral" />
            )}
            {shown.summary.failed > 0 && (
              <SumTile n={shown.summary.failed} label="mit Fehler" tone="danger" />
            )}
          </div>

          {/* Nicht importierte Spalten offenlegen — sonst gehen Daten still verloren */}
          {parsed && parsed.ignored.length > 0 && (
            <div
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                padding: '11px 14px', borderRadius: 10, background: 'var(--bg-muted)',
              }}
            >
              <Info size={15} style={{ color: 'var(--text-tertiary)', flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12.5 }}>
                <strong>Nicht übernommen:</strong> {parsed.ignored.join(', ')}
                <div className="text-tertiary" style={{ marginTop: 2 }}>
                  Diese Spalten kennt die App nicht — die Daten werden ignoriert.
                </div>
              </div>
            </div>
          )}

          {partial && (
            <div
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                padding: '12px 14px', borderRadius: 10,
                background: 'var(--danger-soft, rgba(255,59,48,0.09))',
              }}
            >
              <AlertTriangle size={16} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13.5 }}>{partial}</div>
            </div>
          )}

          {progress && !done && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              <Loader2 size={14} className="spin" style={{ color: 'var(--accent)' }} />
              <span>{progress.done} von {progress.total} Zeilen importiert…</span>
            </div>
          )}

          {done && (
            <div
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                padding: '12px 14px', borderRadius: 10, background: 'var(--bg-muted)',
              }}
            >
              <CheckCircle2 size={16} style={{ color: 'var(--success)', flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13.5 }}>
                Import abgeschlossen. Zeilen mit Fehlern wurden übersprungen — du kannst sie in der
                Datei korrigieren und einfach erneut importieren.
              </div>
            </div>
          )}

          {!done && shown.summary.failed > 0 && (
            <div
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                padding: '12px 14px', borderRadius: 10,
                background: 'var(--danger-soft, rgba(255,59,48,0.09))',
              }}
            >
              <AlertTriangle size={16} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13.5 }}>
                {shown.summary.failed} Zeile{shown.summary.failed === 1 ? '' : 'n'} {shown.summary.failed === 1 ? 'hat' : 'haben'} ein Problem
                und {shown.summary.failed === 1 ? 'wird' : 'werden'} übersprungen. Der Rest lässt sich normal importieren.
              </div>
            </div>
          )}

          {/* Zeilen-Liste */}
          <div
            className="scroll-area"
            style={{
              maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border-subtle)',
              borderRadius: 10,
            }}
          >
            {shown.results.map((r) => {
              const meta = STATUS_META[r.status];
              const Icon = meta.icon;
              return (
                <div
                  key={`${r.zeile}-${r.name}`}
                  className={`import-row ${r.status === 'fehler' ? 'err' : ''}`}
                >
                  <span className="zeile t-mono">{r.zeile}</span>
                  <span className="ico">
                    <Icon
                      size={14}
                      style={{
                        color: meta.tone === 'success' ? 'var(--success)'
                          : meta.tone === 'danger' ? 'var(--danger)'
                          : meta.tone === 'info' ? 'var(--accent)' : 'var(--text-quaternary)',
                      }}
                    />
                  </span>
                  <span className="nm">{r.name}</span>
                  <span className="hint">{r.hinweis}</span>
                  <Badge tone={meta.tone}>{meta.label}</Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}

function SumTile({ n, label, tone }: { n: number; label: string; tone: 'success' | 'info' | 'neutral' | 'danger' }) {
  const color =
    tone === 'success' ? 'var(--success)'
    : tone === 'danger' ? 'var(--danger)'
    : tone === 'info' ? 'var(--accent)'
    : 'var(--text-secondary)';
  return (
    <div
      style={{
        padding: '10px 14px', borderRadius: 10, background: 'var(--bg-muted)',
        minWidth: 104, flex: '0 1 auto',
      }}
    >
      <div style={{ fontSize: 21, fontWeight: 650, letterSpacing: '-0.02em', color }}>{n}</div>
      <div className="text-tertiary" style={{ fontSize: 12 }}>{label}</div>
    </div>
  );
}
